// Shared UI primitives + the "who" speaker dichotomy.
// Languages: we keep a small in-bundle catalog (12 popular ones for fast pickers)
// AND can be hydrated from the server's full /languages response — see useLanguages().
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Icon } from "./icons";
import * as api from "./api";

// ───────── language data (fallback when /languages hasn't loaded) ─────────
export type LangEntry = {
  code: string; short: string; name: string; native: string;
  /** True iff the server has an enrollment prompt pack for this
   *  language. Defaults to true for the bundled fallback list so the
   *  picker is fully functional before /languages hydrates. */
  enrollPrompts?: boolean;
};

export const FALLBACK_LANGS: LangEntry[] = [
  { code: "en-US", short: "EN", name: "English", native: "English", enrollPrompts: true },
  { code: "en-IN", short: "EN", name: "Indian English", native: "English", enrollPrompts: true },
  { code: "hi-IN", short: "HI", name: "Hindi", native: "हिन्दी", enrollPrompts: true },
  { code: "bn-IN", short: "BN", name: "Bengali", native: "বাংলা", enrollPrompts: true },
  { code: "ta-IN", short: "TA", name: "Tamil", native: "தமிழ்", enrollPrompts: true },
  { code: "te-IN", short: "TE", name: "Telugu", native: "తెలుగు" },
  { code: "mr-IN", short: "MR", name: "Marathi", native: "मराठी" },
  { code: "gu-IN", short: "GU", name: "Gujarati", native: "ગુજરાતી" },
  { code: "kn-IN", short: "KN", name: "Kannada", native: "ಕನ್ನಡ" },
  { code: "ml-IN", short: "ML", name: "Malayalam", native: "മലയാളം" },
  { code: "pa-IN", short: "PA", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
  { code: "ur-IN", short: "UR", name: "Urdu", native: "اُردُو" },
  { code: "es-ES", short: "ES", name: "Spanish", native: "Español" },
  { code: "fr-FR", short: "FR", name: "French", native: "Français" },
  { code: "fr-CA", short: "CA", name: "French (Canada)", native: "Français (Canada)" },
  { code: "de-DE", short: "DE", name: "German", native: "Deutsch" },
  { code: "pt-BR", short: "PT", name: "Portuguese", native: "Português" },
  { code: "ja-JP", short: "JA", name: "Japanese", native: "日本語" },
  { code: "zh-CN", short: "ZH", name: "Mandarin", native: "中文" },
  { code: "ar-SA", short: "AR", name: "Arabic", native: "العربية" },
  { code: "ko-KR", short: "KO", name: "Korean", native: "한국어" },
  { code: "it-IT", short: "IT", name: "Italian", native: "Italiano" },
  { code: "ru-RU", short: "RU", name: "Russian", native: "Русский" },
  { code: "fi-FI", short: "FI", name: "Finnish", native: "Suomi" },
  { code: "nb-NO", short: "NB", name: "Norwegian", native: "Norsk" },
];

const NATIVE_NAMES: Record<string, string> = Object.fromEntries(
  FALLBACK_LANGS.map((l) => [l.code, l.native]),
);

let cachedLangs: LangEntry[] = FALLBACK_LANGS;
let cachedAt = 0;

/**
 * Hook that returns a unified language catalog. Falls back to the bundled list
 * on the first render, then hydrates from /languages and caches it.
 */
export function useLanguages(): LangEntry[] {
  const [langs, setLangs] = useState<LangEntry[]>(cachedLangs);
  useEffect(() => {
    if (Date.now() - cachedAt < 5 * 60 * 1000) return;
    api.getLanguages()
      .then((c) => {
        const next = c.languages.map((l) => ({
          code: l.code,
          short: l.code.split("-")[0].toUpperCase(),
          name: l.english_name,
          native: NATIVE_NAMES[l.code] ?? l.english_name,
          enrollPrompts: l.enroll_prompts ?? false,
        }));
        cachedLangs = next;
        cachedAt = Date.now();
        setLangs(next);
      })
      .catch(() => { /* keep fallback */ });
  }, []);
  return langs;
}

export function langByCode(code: string, langs: LangEntry[] = cachedLangs): LangEntry {
  return langs.find((l) => l.code === code) ?? FALLBACK_LANGS[0];
}

// ───────── Button ─────────
type BtnSize = "sm" | "md" | "lg";
type BtnVariant = "primary" | "tonal" | "surface" | "ghost" | "outline";

export function Btn({
  children, variant = "primary", size = "md", full, icon, iconRight,
  onClick, style, disabled, type = "button",
}: {
  children?: ReactNode; variant?: BtnVariant; size?: BtnSize; full?: boolean;
  icon?: string; iconRight?: string;
  onClick?: () => void; style?: CSSProperties; disabled?: boolean; type?: "button" | "submit";
}) {
  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);
  const sizes = {
    sm: { h: 40, px: 16, fs: 14.5, gap: 8, ic: 17 },
    md: { h: 48, px: 22, fs: 15.5, gap: 9, ic: 19 },
    lg: { h: 56, px: 28, fs: 17, gap: 10, ic: 21 },
  }[size];
  const variants: Record<BtnVariant, CSSProperties> = {
    primary: { background: hover ? "var(--accent-press)" : "var(--accent)", color: "var(--on-accent)", border: "none", boxShadow: "var(--shadow-sm)" },
    tonal:   { background: "var(--accent-soft)", color: "var(--accent-ink)", border: "none", filter: hover ? "brightness(0.97)" : "none" },
    surface: { background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--border)", boxShadow: hover ? "var(--shadow-md)" : "var(--shadow-sm)" },
    ghost:   { background: hover ? "var(--surface-2)" : "transparent", color: "var(--ink-2)", border: "none" },
    outline: { background: hover ? "var(--surface-2)" : "transparent", color: "var(--ink)", border: "1px solid var(--border-strong)" },
  };
  return (
    <button type={type} onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => { setHover(false); setPress(false); }}
      onMouseDown={() => setPress(true)} onMouseUp={() => setPress(false)}
      style={{
        height: sizes.h, padding: `0 ${sizes.px}px`, borderRadius: "var(--r-pill)",
        fontSize: sizes.fs, fontWeight: 600, fontFamily: "var(--font-ui)", cursor: disabled ? "default" : "pointer",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: sizes.gap,
        width: full ? "100%" : "auto", opacity: disabled ? 0.45 : 1, whiteSpace: "nowrap",
        transform: press ? "scale(0.975)" : "scale(1)",
        transition: "transform 0.12s var(--ease), background 0.15s, box-shadow 0.15s",
        ...variants[variant], ...style,
      }}>
      {icon && <Icon name={icon} size={sizes.ic} stroke={2.1} />}
      {children}
      {iconRight && <Icon name={iconRight} size={sizes.ic} stroke={2.1} />}
    </button>
  );
}

export function IconBtn({
  name, onClick, size = 42, ic = 20, variant = "ghost", color, title, style,
}: {
  name: string; onClick?: () => void; size?: number; ic?: number;
  variant?: "ghost" | "surface"; color?: string; title?: string; style?: CSSProperties;
}) {
  const [hover, setHover] = useState(false);
  const base = variant === "surface" ? "var(--surface)" : "transparent";
  const bd = variant === "surface" ? "1px solid var(--border)" : "none";
  return (
    <button onClick={onClick} title={title}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: size, height: size, borderRadius: "50%", border: bd,
        background: hover ? "var(--surface-2)" : base,
        color: color || "var(--ink-2)",
        display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
        flexShrink: 0, transition: "background 0.15s", ...style,
      }}>
      <Icon name={name} size={ic} stroke={2.1} />
    </button>
  );
}

// ───────── Field ─────────
export function Field({
  label, icon, value, onChange, placeholder, type = "text", right, hint, mono, autoFocus,
}: {
  label?: string; icon?: string; value: string; onChange?: (v: string) => void;
  placeholder?: string; type?: string; right?: ReactNode; hint?: string;
  mono?: boolean; autoFocus?: boolean;
}) {
  const [focus, setFocus] = useState(false);
  return (
    <label style={{ display: "block" }}>
      {label && <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)", marginBottom: 7 }}>{label}</div>}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, height: 50, padding: "0 14px",
        background: "var(--surface)", borderRadius: "var(--r-md)",
        border: `1.5px solid ${focus ? "var(--accent)" : "var(--border)"}`,
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: focus ? "0 0 0 4px var(--accent-soft)" : "none",
      }}>
        {icon && <Icon name={icon} size={18} color="var(--ink-3)" />}
        <input value={value} onChange={(e) => onChange?.(e.target.value)} placeholder={placeholder} type={type} autoFocus={autoFocus}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} className={mono ? "mono" : ""}
          style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 15.5,
            color: "var(--ink)", fontFamily: mono ? "var(--font-mono)" : "var(--font-ui)", minWidth: 0 }} />
        {right}
      </div>
      {hint && <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>{hint}</div>}
    </label>
  );
}

// ───────── badges / dots ─────────
export type Who = "you" | "guest";

export function LangBadge({ short, who, size = "md" }: { short: string; who?: Who | "neutral"; size?: "sm" | "md" }) {
  const s = size === "sm" ? { w: 26, h: 18, fs: 10.5 } : { w: 32, h: 22, fs: 12 };
  const c = who === "you" ? "var(--you)" : who === "guest" ? "var(--guest)" : "var(--ink-3)";
  return (
    <span className="mono" style={{
      minWidth: s.w, height: s.h, padding: "0 5px", borderRadius: 6, fontSize: s.fs, fontWeight: 600,
      display: "inline-flex", alignItems: "center", justifyContent: "center", letterSpacing: 0.5,
      color: c, background: `color-mix(in oklch, ${c} 14%, transparent)`,
    }}>{short}</span>
  );
}

export function SpeakerDot({ who, size = 10 }: { who: Who; size?: number }) {
  return <span style={{
    width: size, height: size, borderRadius: "50%", flexShrink: 0, display: "inline-block",
    background: who === "you" ? "var(--you)" : "var(--guest)",
  }} />;
}

// ───────── waveform / level / dots ─────────
export function Waveform({ active, color = "currentColor", bars = 5, h = 18, w = 3, gap = 3 }: {
  active?: boolean; color?: string; bars?: number; h?: number; w?: number; gap?: number;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap, height: h }}>
      {Array.from({ length: bars }).map((_, i) => (
        <span key={i} style={{
          width: w, borderRadius: w, background: color, display: "block",
          height: active ? undefined : h * 0.3,
          animation: active ? `hc-wave-${i % 5} 0.9s ${i * 0.08}s ease-in-out infinite` : "none",
        }} />
      ))}
    </div>
  );
}

export function LevelMeter({ level = 0, danger, color = "var(--accent)" }: { level?: number; danger?: boolean; color?: string }) {
  return (
    <div style={{ height: 5, borderRadius: 3, background: "var(--surface-3)", overflow: "hidden", width: "100%" }}>
      <div style={{
        height: "100%", width: `${Math.min(100, level * 100)}%`,
        background: danger ? "var(--bad)" : color, borderRadius: 3, transition: "width 0.1s linear",
      }} />
    </div>
  );
}

export function Dots({ color = "var(--ink-3)" }: { color?: string }) {
  return (
    <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 5, height: 5, borderRadius: "50%", background: color,
          animation: `hc-dots 1.2s ${i * 0.18}s infinite`,
        }} />
      ))}
    </span>
  );
}

// ───────── Modal ─────────
export function Modal({
  open, onClose, children, title, sub, width = 460,
}: {
  open: boolean; onClose?: () => void; children?: ReactNode;
  title?: string; sub?: string; width?: number;
}) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{ position: "absolute", inset: 0, background: "var(--scrim)", animation: "hc-fade-up 0.18s ease", backdropFilter: "blur(2px)" }} />
      <div onClick={(e) => e.stopPropagation()} style={{
        position: "relative", background: "var(--surface)", borderRadius: "var(--r-xl)",
        boxShadow: "var(--shadow-lg)", width, maxWidth: "100%", maxHeight: "86vh",
        display: "flex", flexDirection: "column", border: "1px solid var(--border)",
        animation: "hc-pop 0.28s var(--ease)", overflow: "hidden",
      }}>
        {(title || onClose) && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 22px 12px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {title && <div className="display" style={{ fontSize: 20, fontWeight: 700, color: "var(--ink)", letterSpacing: -0.3 }}>{title}</div>}
              {sub && <div style={{ fontSize: 14, color: "var(--ink-3)", marginTop: 3 }}>{sub}</div>}
            </div>
            {onClose && <IconBtn name="x" onClick={onClose} variant="surface" size={36} ic={18} />}
          </div>
        )}
        <div className="hc-scroll" style={{ overflowY: "auto", minHeight: 0 }}>{children}</div>
      </div>
    </div>
  );
}

// ───────── toggle / segmented ─────────
export function Toggle({ on, onClick }: { on: boolean; onClick?: () => void }) {
  return (
    <button onClick={onClick} style={{
      width: 50, height: 30, borderRadius: "var(--r-pill)", border: "none", cursor: "pointer",
      background: on ? "var(--accent)" : "var(--border-strong)", position: "relative",
      transition: "background 0.2s", flexShrink: 0,
    }}>
      <span style={{
        position: "absolute", top: 3, left: on ? 23 : 3, width: 24, height: 24,
        borderRadius: "50%", background: "#fff", transition: "left 0.2s var(--ease)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
      }} />
    </button>
  );
}

export function Segmented<T extends string>({
  options, value, onChange,
}: { options: T[]; value: T; onChange: (v: T) => void }) {
  return (
    <div style={{ display: "flex", background: "var(--surface-2)", borderRadius: "var(--r-pill)", padding: 3 }}>
      {options.map((o) => (
        <button key={o} onClick={() => onChange(o)} style={{
          padding: "7px 14px", borderRadius: "var(--r-pill)", border: "none", cursor: "pointer",
          fontSize: 13.5, fontWeight: 600, fontFamily: "var(--font-ui)",
          background: value === o ? "var(--surface)" : "transparent",
          color: value === o ? "var(--ink)" : "var(--ink-3)",
          boxShadow: value === o ? "var(--shadow-sm)" : "none", transition: "all 0.15s",
        }}>{o}</button>
      ))}
    </div>
  );
}

// ───────── Language picker list ─────────
export function LangList({
  activeCode, who, onPick, query, setQuery, showRecents, recents,
}: {
  activeCode: string; who: Who;
  onPick: (code: string) => void;
  query: string; setQuery: (q: string) => void;
  showRecents?: boolean; recents?: string[];
}) {
  const langs = useLanguages();
  const q = (query || "").toLowerCase();
  const filtered = langs.filter((l) => (l.name + l.native + l.short).toLowerCase().includes(q));
  const recentCodes = recents ?? ["hi-IN", "es-ES", "fr-FR"];
  const recentItems = recentCodes.map((c) => langByCode(c, langs));
  return (
    <div style={{ padding: "0 0 8px" }}>
      <div style={{ padding: "0 22px 10px" }}>
        <Field icon="search" value={query} onChange={setQuery} placeholder="Search languages" autoFocus />
      </div>
      {showRecents && !query && (
        <div style={{ padding: "2px 22px 8px" }}>
          <div className="mono" style={{ fontSize: 10.5, letterSpacing: 0.6, color: "var(--ink-faint)", textTransform: "uppercase", marginBottom: 8 }}>Recent</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {recentItems.map((l) => (
              <button key={l.code} onClick={() => onPick(l.code)} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                borderRadius: "var(--r-pill)", cursor: "pointer",
                border: `1px solid ${l.code === activeCode ? `var(--${who})` : "var(--border)"}`,
                background: l.code === activeCode ? `var(--${who}-bubble)` : "var(--surface)",
              }}>
                <LangBadge short={l.short} who={who} size="sm" />
                <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{l.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ padding: "4px 12px 8px" }}>
        {filtered.map((l) => {
          const active = l.code === activeCode;
          return (
            <button key={l.code} onClick={() => onPick(l.code)} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 13,
              padding: "11px 12px", borderRadius: 14, cursor: "pointer", border: "none",
              background: active ? `var(--${who}-bubble)` : "transparent", textAlign: "left",
            }}>
              <LangBadge short={l.short} who={who} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15.5, fontWeight: 600, color: "var(--ink)" }}>{l.name}</div>
                <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{l.native}</div>
              </div>
              {active && <Icon name="check" size={20} color={`var(--${who})`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ───────── responsive hook ─────────
export function useMedia(query: string): boolean {
  const [m, setM] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const fn = () => setM(mq.matches);
    mq.addEventListener("change", fn);
    fn();
    return () => mq.removeEventListener("change", fn);
  }, [query]);
  return m;
}

// ───────── theme persistence ─────────
const LS_THEME = "tw.theme";
export function getInitialTheme(): "light" | "dark" {
  const stored = (typeof localStorage !== "undefined" ? localStorage.getItem(LS_THEME) : null) as "light" | "dark" | null;
  if (stored === "light" || stored === "dark") return stored;
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}
export function persistTheme(t: "light" | "dark") {
  try { localStorage.setItem(LS_THEME, t); } catch { /* ignore */ }
}
